import React, { useEffect, useState } from 'react'
import { Col, ProgressBar, Row } from 'react-bootstrap'
import { HiCheckCircle, HiChevronRight } from 'react-icons/hi'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import recommendationsJSON from './recommendations.json'
import s from './recommendations.module.css'

const transformData = (dataArray) => {
  const result = {}

  dataArray.forEach((data, index) => {
    Object.entries(data).forEach(([key, value]) => {
      if (value === 'Yes') {
        if (!result[key]) {
          result[key] = []
        }

        result[key].push({
          id: index,
          name: data.Name,
          objective: data.Description,
          materials: '',
          instructions: [],
          outcome: '',
          time: data.Time,
          zone: data.Zone,
          age: data.Age,
          isCompleted: false,
        })
      }
    })
  })

  return result
}

const data = transformData(recommendationsJSON)

function filterByAge(data, targetAge) {
  // Regex to match age patterns like "03-15", "03-15 years", or "03-15years"
  const ageRangeRegex = /^(\d{1,2})\s*-\s*(\d{1,2})\s*(years)?$/

  return data.filter((item) => {
    const ageString = item.age
    const match = ageString.match(ageRangeRegex)

    if (match) {
      const minAge = parseInt(match[1], 10)
      const maxAge = parseInt(match[2], 10)

      return targetAge >= minAge && targetAge <= maxAge
    }

    return false
  })
}

function getThreeRandomActivitiesByZone(data, existingActivityIds) {
  function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
    return array
  }

  const selectedActivities = []

  const zones = ['green', 'yellow', 'red']

  zones.forEach((zone) => {
    const zoneActivities = data.filter(
      (activity) =>
        activity.zone &&
        activity.zone.toLowerCase() === zone &&
        !existingActivityIds.includes(activity.id)
    )
    const randomActivities = shuffleArray(zoneActivities).slice(0, 3)
    selectedActivities.push(...randomActivities)
  })

  return selectedActivities
}

const RecommendationSystem = () => {
  const [selected, setSelected] = useState('Memory')
  const [activities, setActivities] = useState([])

  const { selectedUser } = useSelector((state) => state.user)

  const loadNewActivities = () => {
    const ageFiltered = filterByAge(data[selected], selectedUser.age)

    let exisitingActivitiesId = activities.map((activity) => activity.id)

    let newActivities = getThreeRandomActivitiesByZone(
      ageFiltered,
      exisitingActivitiesId
    )

    setActivities((oldActivities) => {
      const updatedActivities = [...oldActivities, ...newActivities]
      localStorage.setItem(
        `activities_${selectedUser.id}_${selected}`,
        JSON.stringify(updatedActivities)
      )
      return updatedActivities
    })
  }

  const loadStoredActivities = () => {
    const storedData = JSON.parse(
      localStorage.getItem(`activities_${selectedUser.id}_${selected}`)
    )
    if (storedData) {
      setActivities(storedData)
    } else {
      loadNewActivities()
    }
  }

  useEffect(() => {
    if (!selectedUser) return

    setActivities([])

    loadStoredActivities()
  }, [selected, selectedUser])

  const handleCompleted = (activityName) => {
    setActivities((oldActivities) => {
      const updatedActivities = oldActivities.map((activity) =>
        activity.name === activityName
          ? { ...activity, isCompleted: !activity.isCompleted }
          : activity
      )

      localStorage.setItem(
        `activities_${selectedUser.id}_${selected}`,
        JSON.stringify(updatedActivities)
      )
      return updatedActivities
    })
  }

  const handleSelectedCategory = (category) => {
    setSelected(category)
  }

  const handleGetNewActivities = () => {
    if (activities.every((activity) => activity.isCompleted)) {
      loadNewActivities()
    } else {
      alert('Complete all activities before getting new ones!')
    }
  }

  if (!activities.length) return

  const result = activities.reduce(
    (acc, activity) => {
      acc.total += 1

      acc[activity.zone.toLowerCase()].total += 1

      if (activity.isCompleted) {
        acc.completed += 1
        acc[activity.zone.toLowerCase()].completed += 1
      }

      return acc
    },
    {
      total: 0,
      completed: 0,
      green: { completed: 0, total: 0 },
      yellow: { completed: 0, total: 0 },
      red: { completed: 0, total: 0 },
    }
  )

  const { total, completed, green, yellow, red } = result

  return (
    <>
      <Row className='m-0 p-0 mb-3'>
        {Object.keys(data)?.map((category) => {
          return (
            <Col className='p-0'>
              <button
                onClick={() => handleSelectedCategory(category)}
                className={`border-0 border-3 border-bottom ${
                  selected.toLowerCase() === category.toLowerCase()
                    ? 'border-success'
                    : ' bg-transparent'
                } w-100 text-capitalize py-3 px-4`}
              >
                {category}
              </button>
            </Col>
          )
        })}
      </Row>
      <Row className='m-0'>
        <Col xl={8} className='d-flex flex-column gap-4 p-3'>
          {activities.map((activity, index) => {
            return (
              <ActivityCard
                key={index}
                activity={activity}
                handleCompleted={handleCompleted}
              />
            )
          })}

          <button className='common-btn py-2' onClick={handleGetNewActivities}>
            Get New Activities
          </button>
        </Col>
        <Col xl={4} className='p-3'>
          <p>Overall Progress</p>
          <div
            className='rounded-circle bg-light mx-auto mb-4 d-flex align-items-center justify-content-center'
            style={{
              height: 200,
              width: 200,
              background: `conic-gradient(skyblue ${
                (completed / total) * 360
              }deg, #e9e9e9 0deg)`,
            }}
          >
            <div
              className='rounded-circle mx-auto shadow-sm bg-white d-flex align-items-center justify-content-center'
              style={{ height: 150, width: 150 }}
            >
              <p className='m-0 fs-3'>
                <span className={completed === total ? '' : 'text-secondary'}>
                  {completed}
                </span>
                /{total}
              </p>
            </div>
          </div>

          <div className='w-100'>
            <p>Zone Wise Progress</p>
            <ZoneProgress
              zoneName='Green'
              completed={green.completed}
              total={green.total}
            />
            <ZoneProgress
              zoneName='Yellow'
              completed={yellow.completed}
              total={yellow.total}
            />
            <ZoneProgress
              zoneName='Red'
              completed={red.completed}
              total={red.total}
            />
          </div>
        </Col>
      </Row>
    </>
  )
}

export default RecommendationSystem

const ActivityCard = ({ activity, handleCompleted }) => {
  const navigate = useNavigate()
  return (
    <div className={s.activity_card}>
      <div
        className='flex-grow-1'
        onClick={() =>
          navigate(`${activity.name.toLowerCase().replace(' ', '-')}`, {
            state: { activity },
          })
        }
      >
        <div className='d-flex align-items-center gap-2'>
          {activity.zone.toLowerCase() === 'green'
            ? '🟢'
            : activity.zone.toLowerCase() === 'yellow'
            ? '🟡'
            : '🔴'}

          <span>
            {activity.name}
            <HiChevronRight size={20} fill='black' />
          </span>
        </div>
        <span style={{ fontSize: 14 }}>{activity.time}</span>
      </div>

      <button
        className='border-0 bg-transparent p-0 m-0'
        type='button'
        onClick={() => handleCompleted(activity.name)}
      >
        <HiCheckCircle
          size={24}
          fill={activity.isCompleted ? 'green' : '#c4c4c4'}
        />
      </button>
      {/* <OverlayTrigger placement='auto' delay={{ show: 150, hide: 200 }} overlay={<Tooltip>Mark as Completed</Tooltip>}>
      </OverlayTrigger> */}
    </div>
  )
}

const ZoneProgress = ({ zoneName, completed, total }) => {
  if (total === 0) return null
  return (
    <>
      <div className='d-flex justify-content-between mb-1'>
        <span className='text-secondary'>{zoneName}</span>
        <span>
          <span className={completed === total ? '' : 'text-secondary'}>
            {completed}
          </span>
          /{total}
        </span>
      </div>

      <ProgressBar
        variant={
          zoneName === 'Green'
            ? 'success'
            : zoneName === 'Yellow'
            ? 'warning'
            : 'danger'
        }
        now={(completed / total) * 100}
        className='mb-2 rounded-pill'
        style={{ height: 3 }}
      />
    </>
  )
}
